import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Layout, Input, Button, Typography, Card, Tag, theme } from "antd";
import {
  SearchOutlined,
  ArrowLeftOutlined,
  PlusCircleOutlined,
  CheckCircleOutlined,
  ShopOutlined,
} from "@ant-design/icons";
import { lsSelectedCompany, lsToken } from "../../../constans/auth";
import type { CompanyUser } from "../types";
import { useFetchCompanies } from "../hooks/useFetchCompanies";
import Loading from "../../shared/components/Loading";

const SelectCompany = () => {
  const navigate = useNavigate();
  const { token } = theme.useToken();

  const [search, setSearch] = useState("");
  const [selectedCompany, setSelectedCompany] = useState<CompanyUser | null>(null);

  useEffect(() => {
    const data = localStorage.getItem(lsSelectedCompany);
    if (data) {
      try {
        setSelectedCompany(JSON.parse(data));
      } catch {
        // ignore invalid data
      }
    }
  }, []);

  const { data: baseCompanies, isLoading: isLoadingCompanies } =
    useFetchCompanies() as { data: { data: CompanyUser[] }, isLoading: boolean };

  useEffect(() => {
    if (baseCompanies?.data?.length === 0) {
      navigate("/empresa/nueva");
    }
  }, [baseCompanies, navigate]);

  if (isLoadingCompanies) {
    return <Loading />;
  }

  const companies =
    baseCompanies?.data?.map((company: CompanyUser) => ({
      ...company,
      isCurrent: selectedCompany?.id_company === company.id_company,
    })) ?? [];

  const filteredCompanies = companies.filter((company: CompanyUser) =>
    company.name_company.toLowerCase().includes(search.toLowerCase())
  );

  const handleCompanySelect = (company: CompanyUser) => {
    localStorage.setItem(lsToken, company.token);
    localStorage.setItem(lsSelectedCompany, JSON.stringify(company));

    navigate("/dashboard");
  };

  return (
    <>
      <Layout.Header
        style={{
          background: token.colorBgLayout,
          padding: "24px 16px 24px",
          position: "sticky",
          top: 0,
          zIndex: 50,
          marginBottom: "16px",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            marginBottom: "16px",
          }}
        >
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate(-1)}
            style={{
              width: "40px",
              height: "40px",
              borderRadius: "50%",
              color: token.colorTextSecondary,
            }}
          />
          <Typography.Title
            level={3}
            style={{ margin: 0, fontWeight: "bold", color: token.colorTextHeading }}
          >
            Seleccionar Empresa
          </Typography.Title>
        </div>
      </Layout.Header>

      <Layout.Content style={{ padding: "8px 16px", flex: 1 }}>
        <section style={{ marginBottom: "16px" }}>
          <Input
            placeholder="Buscar empresa..."
            prefix={<SearchOutlined style={{ color: token.colorTextPlaceholder }} />}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              borderRadius: "12px",
              background: token.colorBgContainer,
              border: "none",
              boxShadow: "0 1px 2px rgba(0, 0, 0, 0.1)",
            }}
          />
        </section>

        {filteredCompanies.map((company: CompanyUser) => {
          const IconComponent = ShopOutlined;
          const color = token.colorPrimary;

          return (
            <Card
              key={company.id_company}
              hoverable
              style={{
                marginBottom: "12px",
                borderRadius: "12px",
                cursor: "pointer",
                border: company.isCurrent
                  ? `2px solid ${token.colorPrimaryBorder}`
                  : `1px solid ${token.colorBorderSecondary}`,
                background: token.colorBgContainer,
              }}
              bodyStyle={{ padding: "16px" }}
              onClick={() => handleCompanySelect(company)}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <div
                  style={{ display: "flex", alignItems: "center", gap: "16px" }}
                >
                  <div
                    style={{
                      width: "48px",
                      height: "48px",
                      borderRadius: "12px",
                      backgroundColor: color + "10",
                      border: `1px solid ${color}20`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: color,
                    }}
                  >
                    <IconComponent style={{ fontSize: "26px" }} />
                  </div>

                  <div>
                    <Typography.Text
                      strong
                      style={{ fontSize: "16px", color: token.colorText }}
                    >
                      {company.name_company}
                    </Typography.Text>
                    <br />
                    <Typography.Text
                      style={{ fontSize: "12px", color: token.colorTextSecondary }}
                    >
                      {company.rfc_company} • ID: {company.id_company}
                    </Typography.Text>
                  </div>
                </div>

                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-end",
                    gap: "4px",
                  }}
                >
                  {company.isCurrent && (
                    <Tag
                      color="blue"
                      style={{
                        fontSize: "10px",
                        fontWeight: "bold",
                        padding: "2px 8px",
                      }}
                    >
                      Actual
                    </Tag>
                  )}

                  <CheckCircleOutlined
                    style={{
                      fontSize: "20px",
                      color: company.isCurrent ? token.colorPrimary : token.colorTextSecondary,
                    }}
                  />
                </div>
              </div>
            </Card>
          );
        })}
      </Layout.Content>

      <Layout.Footer
        style={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          maxWidth: "28rem",
          margin: "0 auto",
          padding: "24px 16px",
          background: (token as any).customBgGradientFooter || "linear-gradient(to top, white, rgba(255,255,255,0.95))",
          backdropFilter: "blur(4px)",
          borderTop: `1px solid ${token.colorBorderSecondary}`,
          zIndex: 40,
        }}
      >
        <Link to="/empresa/nueva">
          <Button
            type="primary"
            icon={<PlusCircleOutlined />}
            style={{
              width: "100%",
              height: "48px",
              fontSize: "14px",
              fontWeight: "bold",
              borderRadius: "12px",
              backgroundColor: token.colorPrimary,
              borderColor: token.colorPrimary,
              boxShadow: "0 10px 15px rgba(0, 0, 0, 0.15)",
            }}
          >
            Agregar Nueva Empresa
          </Button>
        </Link>
      </Layout.Footer>
    </>
  );
};

export default SelectCompany;